import { isExpired } from '@stayon/auth-core';
import { supportSessionOf, type SupportTokenSource } from '@/shared/api/supportBearer';
import type { SupportSessionApi } from '@/shared/api/supportSessionEndpoints';
import type { SupportSessionResponse } from '@/shared/api/types';
import { supportEndReason } from './supportFailure';
import type { SupportEndReason } from './supportNotices';
import { lastsUntilSessionEnd } from './supportResume';

export interface ActiveSupportSession {
  view: SupportSessionResponse;
  token: string;
  /** Die Heimatsitzung, die die Sitzung begonnen oder nach dem Neuladen übernommen hat. */
  owner: string;
}

interface RenewalOptions {
  api: () => SupportSessionApi;
  active: () => ActiveSupportSession | null;
  onRenewed: () => void;
  finish: (reason: SupportEndReason, sessionId: string) => void;
}

/**
 * Neue Tokens der laufenden Sitzung über die Heimatsitzung, für gleichzeitige Requests genau
 * einmal. Liefert `null`, sobald die Sitzung, die ein Token ausgestellt hat, nicht mehr läuft.
 */
export function createSupportRenewal({ api, active, onRenewed, finish }: RenewalOptions) {
  let flight: Promise<string | null> | null = null;

  async function mint({ view }: ActiveSupportSession): Promise<string | null> {
    try {
      const access = await api().issueToken(view.sessionId);
      const session = active();
      if (session?.view.sessionId !== view.sessionId) return null;
      session.token = access.accessToken;
      onRenewed();
      return access.accessToken;
    } catch (error) {
      const reason = await supportEndReason(error, view.expiresAt);
      if (reason === null) throw error;
      finish(reason, view.sessionId);
      return null;
    }
  }

  function renew(stale: string, rejected: boolean): Promise<string | null> {
    const session = active();
    if (session?.view.sessionId !== supportSessionOf(stale)) return Promise.resolve(null);
    if (session.token !== stale && !isExpired(session.token)) return Promise.resolve(session.token);
    if (!rejected && lastsUntilSessionEnd(session.token, session.view.expiresAt)) {
      if (!isExpired(session.token, 0)) return Promise.resolve(session.token);
      finish('expired', session.view.sessionId);
      return Promise.resolve(null);
    }
    if (!flight) {
      const current: Promise<string | null> = mint(session).finally(() => {
        if (flight === current) flight = null;
      });
      flight = current;
    }
    return flight;
  }

  const tokens: SupportTokenSource = { current: () => active()?.token ?? null, renew };
  return {
    tokens,
    reset: () => {
      flight = null;
    },
  };
}
